/**
 * @fileoverview Pre-Appointment custom fields manager (MOR-948)
 *
 * Firm-defined attributes tracked against pre-appointment jobs — e.g. a fee
 * estimate — edited from the pipeline job panel. Admin/owner only; `canEdit`
 * gates writes and the mutations enforce the same rule.
 *
 * Visual language mirrors the Pipeline Labels manager for consistency.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@cvx/_generated/api";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import type { PreAppointmentFieldType } from "@cvx/preAppointment/preAppointmentFields/schema";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { cn } from "@/utils/misc";
import { ColorSwatchPicker } from "../tasks/ColorSwatchPicker";
import { CustomFieldBadge } from "@/components/pre-appointment/custom-field-badge";
import {
  PRE_APPOINTMENT_FIELD_ICON_NAMES,
  fieldIcon,
} from "@/components/pre-appointment/custom-fields";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

const FIELD_TYPE_LABELS: Record<PreAppointmentFieldType, string> = {
  text: "Text",
  number: "Number",
  currency: "Currency",
  date: "Date",
};

type PreAppointmentField = FunctionReturnType<
  typeof api.preAppointment.fields.listByOrg
>[number];

export function PreAppointmentFieldsManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: fields } = useQuery(
    convexQuery(api.preAppointment.fields.listByOrg, { orgId }),
  );
  const reorder = useConvexMutation(api.preAppointment.fields.reorder);
  const [showNewForm, setShowNewForm] = useState(false);
  const rows = useMemo(() => fields ?? [], [fields]);

  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => a.sortOrder - b.sortOrder),
    [rows],
  );

  // Drag-to-reorder (mirrors the labels manager). A 6px activation distance
  // keeps row clicks/buttons usable; only the grip handle starts a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = orderedRows.map((f) => f._id);
    const from = ids.indexOf(active.id as Id<"preAppointmentFields">);
    const to = ids.indexOf(over.id as Id<"preAppointmentFields">);
    if (from < 0 || to < 0) return;
    reorder({ orgId, orderedIds: arrayMove(ids, from, to) }).catch(
      (err: Error) => toast.error(err.message || "Could not reorder fields."),
    );
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="flex items-start justify-between gap-6 border-b border-border/50 p-6">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-primary">Custom Fields</h3>
          <p className="mt-1 text-sm text-primary/60">
            Add custom fields to your pre-appointment jobs (e.g. estimated
            fees). Custom fields will appear on every pipeline job record. Drag
            fields to reorder them.
          </p>
        </div>
        <Badge variant="secondary" className="shrink-0">
          {rows.length} {rows.length === 1 ? "field" : "fields"}
        </Badge>
      </div>

      <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
        <h4 className="text-sm font-semibold text-primary">Fields</h4>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewForm(true)}
            className="gap-1.5"
          >
            <Plus className="size-3.5" />
            Add field
          </Button>
        )}
      </div>

      <div className="divide-y divide-border/30">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedRows.map((f) => f._id)}
            strategy={verticalListSortingStrategy}
          >
            {orderedRows.map((field, index) => (
              <FieldRow
                key={field._id}
                orgId={orgId}
                field={field}
                canEdit={canEdit}
                isFirst={index === 0}
                isLast={index === orderedRows.length - 1}
                orderedIds={orderedRows.map((f) => f._id)}
                index={index}
              />
            ))}
          </SortableContext>
        </DndContext>

        {rows.length === 0 && !showNewForm && (
          <div className="px-6 py-8 text-center">
            <p className="text-sm text-primary/40">No custom fields yet.</p>
          </div>
        )}

        {showNewForm && (
          <NewFieldForm orgId={orgId} onClose={() => setShowNewForm(false)} />
        )}
      </div>
    </div>
  );
}

function FieldRow({
  orgId,
  field,
  canEdit,
  isFirst,
  isLast,
  orderedIds,
  index,
}: {
  orgId: Id<"organizations">;
  field: PreAppointmentField;
  canEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  orderedIds: Array<Id<"preAppointmentFields">>;
  index: number;
}) {
  const [editing, setEditing] = useState(false);
  const update = useConvexMutation(api.preAppointment.fields.update);
  const reorder = useConvexMutation(api.preAppointment.fields.reorder);
  const archive = useConvexMutation(api.preAppointment.fields.archive);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field._id, disabled: !canEdit || editing });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const move = (direction: -1 | 1) => {
    const next = [...orderedIds];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder({ orgId, orderedIds: next }).catch((e: Error) =>
      toast.error(e.message || "Could not reorder fields."),
    );
  };

  if (editing) {
    return (
      <div ref={setNodeRef} style={style} className="px-6 py-4">
        <FieldForm
          initial={{
            name: field.name,
            type: field.type,
            color: field.color,
            icon: field.icon ?? "",
          }}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (state) => {
            await update({
              fieldId: field._id,
              patch: {
                name: state.name.trim(),
                type: state.type,
                color: state.color,
                icon: state.icon,
              },
            });
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 bg-background px-6 py-3",
        isDragging && "relative z-10 shadow-lg",
      )}
    >
      {canEdit && (
        <span
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-primary/25 hover:text-primary/60 active:cursor-grabbing"
          title="Drag to reorder"
          aria-label={`Drag ${field.name} to reorder`}
        >
          <GripVertical className="size-4" />
        </span>
      )}
      {canEdit && (
        <div className="flex flex-col">
          <button
            type="button"
            disabled={isFirst}
            onClick={() => move(-1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move up"
          >
            <ArrowUp className="size-3" />
          </button>
          <button
            type="button"
            disabled={isLast}
            onClick={() => move(1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move down"
          >
            <ArrowDown className="size-3" />
          </button>
        </div>
      )}
      <span className="text-sm font-medium">{field.name}</span>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Badge variant="outline" size="sm">
          {FIELD_TYPE_LABELS[field.type]}
        </Badge>
        {/* Live badge — exactly how this field reads on a pipeline card. */}
        <CustomFieldBadge
          field={field}
          value={SAMPLE_VALUE[field.type]}
          showName
        />
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-destructive hover:text-destructive"
            onClick={() => {
              archive({ fieldId: field._id }).catch((e: Error) =>
                toast.error(e.message || "Could not remove field."),
              );
            }}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function NewFieldForm({
  orgId,
  onClose,
}: {
  orgId: Id<"organizations">;
  onClose: () => void;
}) {
  const create = useConvexMutation(api.preAppointment.fields.create);
  return (
    <div className="bg-muted/20 px-6 py-4">
      <FieldForm
        initial={{ name: "", type: "text", color: "blue", icon: "tag" }}
        submitLabel="Add field"
        onCancel={onClose}
        onSubmit={async (state) => {
          await create({
            orgId,
            name: state.name.trim(),
            type: state.type,
            color: state.color,
            icon: state.icon || undefined,
          });
          onClose();
        }}
      />
    </div>
  );
}

/** Placeholder value used for the badge preview in the manager. */
const SAMPLE_VALUE: Record<PreAppointmentFieldType, string> = {
  text: "Example",
  number: "12",
  currency: "25000",
  date: "2026-08-21",
};

interface FieldFormState {
  name: string;
  type: PreAppointmentFieldType;
  color: TaskColor;
  icon: string;
}

function FieldForm({
  initial,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: FieldFormState;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (state: FieldFormState) => Promise<void>;
}) {
  const [state, setState] = useState<FieldFormState>(initial);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!state.name.trim()) {
      toast.error("Field name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(state);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not save field."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Name
        </Label>
        <Input
          value={state.name}
          autoFocus
          onChange={(e) => setState({ ...state, name: e.target.value })}
          placeholder="Fee estimate, Expected appointment date…"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Type
        </Label>
        <Select
          value={state.type}
          onValueChange={(type) =>
            setState({ ...state, type: type as PreAppointmentFieldType })
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(
              Object.entries(FIELD_TYPE_LABELS) as Array<
                [PreAppointmentFieldType, string]
              >
            ).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Colour
        </Label>
        <ColorSwatchPicker
          value={state.color}
          onChange={(color) => setState({ ...state, color })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Icon
        </Label>
        <div className="flex flex-wrap gap-1">
          {PRE_APPOINTMENT_FIELD_ICON_NAMES.map((name) => {
            const Icon = fieldIcon(name);
            return (
              <button
                key={name}
                type="button"
                aria-label={name}
                title={name}
                onClick={() => setState({ ...state, icon: name })}
                className={cn(
                  "flex size-7 items-center justify-center rounded-md border transition",
                  state.icon === name
                    ? "border-primary bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                <Icon className="size-3.5" />
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Preview</span>
        <CustomFieldBadge
          field={{
            _id: "preview",
            name: state.name.trim() || "Field",
            type: state.type,
            color: state.color,
            icon: state.icon || undefined,
          }}
          value={SAMPLE_VALUE[state.type]}
          showName
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="gap-1">
          <X className="size-3.5" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting}
          className="gap-1.5"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
